import type { ResendMagicLinkEmailMutationOptions } from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { UUID } from '~/types/common';

import { queryKeysCallOff } from '../queryKeys';

import { resendMagicLinkEmail } from './resendMagicLinkEmail';

/**
 * Mutation key for resending magic link email.
 * Used with useMutationState to track pending resend actions.
 */
export const MUTATION_KEY_RESEND_MAGIC_LINK = [
  'calloffs',
  'resendMagicLink',
] as const;

/**
 * Custom hook for resending a magic link email to a supplier.
 * Regenerates token and resends email. Only valid for REQUESTED status.
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Off-Magic-Link/operation/resend_magic_link_email_calloffs__calloff_id__magic_link_resend_post
 */
export const useMutationResendMagicLinkEmail = (
  options?: ResendMagicLinkEmailMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (callOffId: UUID) => resendMagicLinkEmail(callOffId),
    mutationKey: MUTATION_KEY_RESEND_MAGIC_LINK,
    onSuccess(_data, callOffId) {
      queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.get(callOffId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getAll(undefined),
      });
    },
    ...options,
  });
};
